import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ArticleCard from "@/components/base/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/gossip";
import { formatCount } from "@/utils/format";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const related = useMemo(() => {
    if (!article) return [];
    const sameChannel = articles.filter((a) => a.channelSlug === article.channelSlug && a.id !== article.id);
    const others = articles.filter((a) => a.channelSlug !== article.channelSlug && a.id !== article.id);
    return [...sameChannel, ...others].slice(0, 3);
  }, [article]);

  const ranking = useMemo(() => [...articles].sort((a, b) => b.hot - a.hot).slice(0, 5), []);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "zh-CN",
        articleSection: article.channelName,
        keywords: article.tags.join(","),
        author: { "@type": "Organization", name: "看料社" },
        publisher: { "@type": "Organization", name: "看料社" },
        image: article.cover,
        mainEntityOfPage: `https://sdyjh.cn/article/${article.id}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sdyjh.cn/" },
          { "@type": "ListItem", position: 2, name: "频道入口大全", item: "https://sdyjh.cn/channels" },
          { "@type": "ListItem", position: 3, name: article.title, item: `https://sdyjh.cn/article/${article.id}` },
        ],
      },
    ];
  }, [article]);

  useSeo({
    title: article ? `${article.title} - 看料爆料 | 看料社` : "爆料详情 - 看料社",
    description: article
      ? article.summary
      : "看料爆料专区内容整理详情页，集中呈现娱乐爆料版块与频道的最新条目。",
    keywords: article ? `${article.channelName},${article.tags.join(",")},看料爆料` : "看料爆料",
    canonicalPath: `/article/${id ?? ""}`,
    ogType: "article",
    jsonLd,
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-32">
          <div className="text-center max-w-md">
            <span className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line" />
            </span>
            <h1 className="mt-6 font-heading font-bold text-2xl text-foreground-950">这条爆料还没整理好</h1>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              可能是链接过期，或者内容还在编辑整理。你可以回到频道入口大全，从其他方向继续浏览。
            </p>
            <Link
              to="/channels"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回频道入口大全
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-14 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            <article className="lg:col-span-8">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "频道入口大全", to: "/channels" },
                  { label: article.channelName, to: "/channels" },
                  { label: "爆料详情" },
                ]}
              />

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs bg-primary-100 text-primary-700 whitespace-nowrap">
                  {article.channelName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-5 font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">
                {article.title}
              </h1>

              <div className="mt-5 flex items-center gap-4 flex-wrap text-sm text-foreground-500 pb-6 border-b border-background-200">
                <span className="flex items-center gap-1.5"><i className="ri-user-line" />{article.author}</span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line" />{article.date}</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line" />阅读约 {article.readTime}</span>
                <span className="flex items-center gap-1.5"><i className="ri-eye-line" />{formatCount(article.views)} 次浏览</span>
              </div>

              <div className="mt-7 w-full h-64 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={`${article.title}相关配图`}
                  title={`${article.title} - 看料爆料`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="mt-8 space-y-5">
                {article.content.map((para, index) => (
                  <p key={index} className="text-base md:text-lg text-foreground-700 leading-loose">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-10 p-5 md:p-6 rounded-lg bg-secondary-100 border border-secondary-200">
                <h2 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-information-line text-secondary-700" />
                  内容说明
                </h2>
                <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">
                  本文由看料社编辑部整理自公开渠道的公开信息，整理时间为 {article.date}。文中涉及尚未证实的说法均已标注，仅作资讯参考，不构成任何结论性判断。
                </p>
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-background-200">
                <Link
                  to="/channels"
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回频道入口
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  了解内容原则
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 flex flex-col gap-6">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                  <h2 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2 mb-4">
                    <i className="ri-fire-line text-primary-500" />
                    热榜速览
                  </h2>
                  <ol className="flex flex-col gap-3">
                    {ranking.map((item, index) => (
                      <li key={item.id} className="flex gap-3">
                        <span
                          className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Link
                          to={`/article/${item.id}`}
                          className="text-sm text-foreground-700 hover:text-primary-600 transition-colors leading-snug line-clamp-2 cursor-pointer"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="p-5 rounded-lg bg-accent-100 border border-accent-200">
                  <h2 className="font-heading font-semibold text-base text-foreground-950">按板块继续翻</h2>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    想看别的方向？频道入口大全按频道、版块、栏目三类整理，找起来更快。
                  </p>
                  <Link
                    to="/channels"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    进入频道入口大全
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14 md:mt-20 pt-10 border-t border-background-200">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">相关爆料推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}